import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { ContentBody } from 'areas/shared/components';
import { Box, Button } from 'fw/components';
import * as Notifications from 'fw/globals/Notifications';
import * as Modals from 'fw/globals/Modals';
import * as FormManager from 'fw/forms/form/FormManager';
import * as API from 'modules/app/API';
import CompanySection from './sections/CompanySection';
import VeteranOwnersSection from './sections/VeteranOwnersSection';
import BusinessStructureSection from './sections/BusinessStructureSection';
import BusinessCertificationSection from './sections/BusinessCertificationSection';
import FinancialsSection from './sections/FinancialsSection';
import './OneSheetRenewal.css';

// NVBDC-1404 — condensed single-page renewal form. Every section stages edits into local
// component state only (pencil popups, inline "No" edits, the NAICS picker) — nothing hits the
// backend until Save/Submit, which sends the whole page through SaveOneSheet in one call. See
// nvbdc-docs/plans/NVBDC-1404-plan.md ("Atomicity design (final)") for why the backend is safe to
// call this way without touching any existing per-step method.
class OneSheetRenewal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: null,
            loading: true,
            saving: false
        };
    }

    componentDidMount() {
        this.loadOneSheet();
        // Same shared mechanism Certification.js's Cancel button uses (FormManager.checkUnsavedChanges)
        // — the one-sheet doesn't wrap its sections in fw/forms/form/Form (which normally registers
        // itself via unsavedChangesWarning), so it registers directly with the manager instead. This
        // keeps the warning's wording/behavior identical to every other unsaved-changes prompt in the
        // app rather than inventing a separate one.
        FormManager.add(this);
    }

    componentWillUnmount() {
        FormManager.remove(this);
    }

    loadOneSheet = () => {
        const rq = { certificationKey: this.props.match.params.certificationKey };

        API.Certification.get('/certificationSetup/getOneSheet', rq, {
            success: r => {
                // GetOneSheet's OwnershipChange is the recovered reviewer-log history ("has this
                // ever been disclosed") — kept separately, read-only, as ownershipChangeHistory.
                // The editable form field itself always starts blank/No: toggling to Yes is always
                // a fresh disclosure that logs a NEW reviewer-log entry, never an edit of a past one.
                this.setState({
                    data: {
                        ...r.data,
                        ownershipChangeHistory: r.data.ownershipChange,
                        ownershipChange: { changed: false, description: null, storageToken: null, fileName: null }
                    },
                    loading: false
                });
            },
            error: r => {
                this.setState({ loading: false });
                Notifications.error(r.error.errorMessage);
            }
        }, { block: true });
    };

    // The single choke point every section's onChange funnels through (Company Info, Veteran
    // Owners incl. the disability toggle, Business Structure popups, Business Certifications,
    // Declaration upload) — marking dirty here covers all of them without touching each section.
    updateSection = (key, value) => {
        FormManager.dirt(this);

        this.setState(prevState => ({
            data: { ...prevState.data, [key]: value }
        }));
    };

    onVobInfoChange = (vobInfo) => this.updateSection('vobInfo', vobInfo);

    onVeteranOwnerListChange = (veteranOwnerList) => {
        this.updateSection('veteranOwnerInfo', { ...this.state.data.veteranOwnerInfo, veteranOwnerList });
    };

    onOwnershipChangeChange = (ownershipChange) => this.updateSection('ownershipChange', ownershipChange);

    onBusinessStructureChange = (businessStructure) => this.updateSection('businessStructure', businessStructure);

    onBusinessCertificationChange = (businessCertification) => this.updateSection('businessCertification', businessCertification);

    onDeclarationChange = (declaration) => this.updateSection('declaration', declaration);

    buildSaveOneSheetRequest = (submit) => {
        const { data } = this.state;

        return {
            certificationKey: this.props.match.params.certificationKey,
            vobInfo: data.vobInfo && {
                entityId: data.vobInfo.entityId,
                certificationId: data.certificationId,
                vobName: data.vobInfo.vobName,
                website: data.vobInfo.website,
                email: data.vobInfo.email,
                businessAddress: data.vobInfo.businessAddress,
                mailingAddress: data.vobInfo.mailingAddress,
                sameMailingAddress: data.vobInfo.sameMailingAddress,
                phones: data.vobInfo.phones || [],
                vobDba: data.vobInfo.vobDba || [],
                dba: data.vobInfo.dba,
                businessOwnershipId: data.vobInfo.businessOwnershipId,
                geographicMarketId: data.vobInfo.geographicMarketId,
                businessPremisesId: data.vobInfo.businessPremisesId,
                businessAcquirementId: data.vobInfo.businessAcquirementId,
                yearBusinessFormed: data.vobInfo.yearBusinessFormed,
                yearVeteranBusiness: data.vobInfo.yearVeteranBusiness,
                grossRevenue1Year: data.vobInfo.grossRevenue1Year,
                grossRevenue1Amount: data.vobInfo.grossRevenue1Amount,
                grossRevenue2Year: data.vobInfo.grossRevenue2Year,
                grossRevenue2Amount: data.vobInfo.grossRevenue2Amount,
                grossRevenueDocuments: data.vobInfo.grossRevenueDocuments || [],
                rowVersion: data.vobInfo.rowVersion
            },
            veteranOwnerInfo: data.veteranOwnerInfo && {
                certificationId: data.certificationId,
                veteranOwnerList: data.veteranOwnerInfo.veteranOwnerList,
                pointOfContactList: data.veteranOwnerInfo.pointOfContactList
            },
            businessStructure: data.businessStructure && {
                entityId: data.businessStructure.entityId,
                certificationId: data.certificationId,
                capabilitiesStatement: data.businessStructure.capabilitiesStatement,
                capabilitiesStatementStorageToken: data.businessStructure.capabilitiesStatementStorageToken,
                capabilitiesStatementFileName: data.businessStructure.capabilitiesStatementFileName,
                professionalAccreditation: data.businessStructure.professionalAccreditation,
                professionalAccreditationStorageToken: data.businessStructure.professionalAccreditationStorageToken,
                professionalAccreditationFileName: data.businessStructure.professionalAccreditationFileName,
                amountFullTimeEmployee: data.businessStructure.amountFullTimeEmployee,
                amountVeteranEmployee: data.businessStructure.amountVeteranEmployee,
                amountPartTimeEmployee: data.businessStructure.amountPartTimeEmployee,
                amountContractor: data.businessStructure.amountContractor,
                naicsCodes: (data.businessStructure.naicsCodes || []).map(x => ({
                    businessStructureNAICSCodeId: x.businessStructureNAICSCodeId || 0,
                    naicsCodeId: x.naicsCodeId
                })),
                // The one-sheet has no editor for per-Business-Function staff assignment
                // (NVBDC-1404/GAP-1404-02) — omitted so the backend leaves whatever staff is
                // already on the record (real data for a renewal, nothing for a first-time BS
                // save) untouched instead of round-tripping GetBusinessStructure's placeholders.
                rowVersion: data.businessStructure.rowVersion
            },
            businessCertification: data.businessCertification && {
                entityId: data.businessCertification.entityId,
                certificationId: data.certificationId,
                nmsdc: data.businessCertification.nmsdc,
                nmsdcStorageToken: data.businessCertification.nmsdcStorageToken,
                nmsdcFileName: data.businessCertification.nmsdcFileName,
                nmsdcExpirationDate: data.businessCertification.nmsdcExpirationDate,
                wbenc: data.businessCertification.wbenc,
                wbencStorageToken: data.businessCertification.wbencStorageToken,
                wbencFileName: data.businessCertification.wbencFileName,
                wbencExpirationDate: data.businessCertification.wbencExpirationDate,
                nglcc: data.businessCertification.nglcc,
                nglccStorageToken: data.businessCertification.nglccStorageToken,
                nglccFileName: data.businessCertification.nglccFileName,
                nglccExpirationDate: data.businessCertification.nglccExpirationDate,
                cve: data.businessCertification.cve,
                sba8: data.businessCertification.sba8,
                sba: data.businessCertification.sba,
                dvbe: data.businessCertification.dvbe,
                naVOBA: data.businessCertification.naVOBA,
                di: data.businessCertification.di,
                none: data.businessCertification.none,
                otherFiles: data.businessCertification.otherFiles || [],
                rowVersion: data.businessCertification.rowVersion
            },
            declaration: data.declaration && {
                certificationId: data.certificationId,
                declarationStorageToken: data.declaration.declarationStorageToken,
                declarationFileName: data.declaration.declarationFileName,
                rowVersion: data.declaration.rowVersion
            },
            ownershipChange: data.ownershipChange && {
                changed: data.ownershipChange.changed,
                description: data.ownershipChange.description,
                storageToken: data.ownershipChange.storageToken,
                fileName: data.ownershipChange.fileName
            },
            submit: submit
        };
    };

    handleSave = () => {
        this.save(false, r => {
            FormManager.reset();
            Notifications.success(r.message);
            this.loadOneSheet();
        });
    };

    handleSubmit = () => {
        Modals.confirm(
            'Submit this renewal now? You will not be able to make further changes.',
            () => {
                this.save(true, () => {
                    FormManager.reset();
                    Modals.info('Renewal Submitted', 'Thank you — your renewal has been submitted for review.', () => {
                        this.props.history.push('/');
                    });
                });
            },
            () => {}
        );
    };

    // SaveOneSheet's Phase 2 isn't one atomic transaction across sections — each section's Save*
    // method (SaveVobInfo, SaveVeteranOwnerInfo, SaveBusinessStructure, SaveBusinessCertification,
    // SaveDeclaration) commits to the DB independently as it runs, and only the final
    // SubmitCertification validation (missing upload, incomplete step, etc.) can still fail the
    // overall call afterward. When that happens, the earlier sections' writes already landed and
    // their RowVersion already advanced in the DB, while this component's local copy — captured once
    // at page load — doesn't. Reported live: fix the flagged issue and Submit again (without an
    // intervening full-form reload) and the *next* attempt trips "This Company was changed by another
    // user", even though it was this same user's own prior attempt. SaveOneSheet always returns the
    // current per-section RowVersion in r.data, even on a failed overall response
    // (CertificationSetupService.cs SaveOneSheet, VobRowVersion/VeteranOwnerRowVersions/
    // BusinessStructureRowVersion/BusinessCertificationRowVersion/DeclarationRowVersion) — sync those
    // into local state on every error so a retry uses current values instead of stale ones.
    //
    // VeteranOwnerInfo is a list of N owners, each independently RowVersioned (VeteranOwnerRowVersion
    // above is always 0 — UpdateVeteranOwnerInfo has no single meaningful scalar to report — and was
    // previously left out of this sync for exactly that reason). The backend now also returns
    // VeteranOwnerRowVersions: [{ veteranOwnerId, rowVersion }, ...] reflecting every owner's real
    // post-save RowVersion, so each local owner can be resynced by matching its own id — the same
    // partial-success-then-retry scenario as the other four sections, just keyed per owner instead of
    // one scalar. Owners the response doesn't mention (e.g. a brand-new owner added locally but never
    // reached because an earlier section failed first) are left untouched.
    syncRowVersions = (responseData) => {
        this.setState(prevState => {
            const d = prevState.data;
            if (!d) return null;

            const freshVeteranOwnerRowVersions = responseData.veteranOwnerRowVersions;

            return {
                data: {
                    ...d,
                    vobInfo: (d.vobInfo && responseData.vobRowVersion != null)
                        ? { ...d.vobInfo, rowVersion: responseData.vobRowVersion } : d.vobInfo,
                    veteranOwnerInfo: (d.veteranOwnerInfo && freshVeteranOwnerRowVersions)
                        ? {
                            ...d.veteranOwnerInfo,
                            veteranOwnerList: d.veteranOwnerInfo.veteranOwnerList.map(owner => {
                                const fresh = freshVeteranOwnerRowVersions.find(x => x.veteranOwnerId === owner.veteranOwnerId);
                                return fresh ? { ...owner, rowVersion: fresh.rowVersion } : owner;
                            })
                        }
                        : d.veteranOwnerInfo,
                    businessStructure: (d.businessStructure && responseData.businessStructureRowVersion != null)
                        ? { ...d.businessStructure, rowVersion: responseData.businessStructureRowVersion } : d.businessStructure,
                    businessCertification: (d.businessCertification && responseData.businessCertificationRowVersion != null)
                        ? { ...d.businessCertification, rowVersion: responseData.businessCertificationRowVersion } : d.businessCertification,
                    declaration: (d.declaration && responseData.declarationRowVersion != null)
                        ? { ...d.declaration, rowVersion: responseData.declarationRowVersion } : d.declaration
                }
            };
        });
    };

    save = (submit, onSuccess) => {
        if (this.state.saving) return;

        const rq = this.buildSaveOneSheetRequest(submit);
        this.setState({ saving: true });

        API.Certification.post('/certificationSetup/saveOneSheet', rq, {
            success: (r) => {
                this.setState({ saving: false });
                onSuccess(r);
            },
            error: r => {
                this.setState({ saving: false });
                Notifications.error(r.error.errorMessage);

                if (r.data)
                    this.syncRowVersions(r.data);

                // Only reload the whole form from the server on a genuine concurrency conflict (a
                // rowVersion still stale even after the sync above — i.e. someone/something else truly
                // touched it) — that's the only failure where retrying with local state would still
                // fail, and Messages.Validation.ConcurrencyProblem(...) is the one message that always
                // contains this phrase, whichever section/entity it's for. An ordinary validation
                // error (e.g. a required field left blank, or the ownership-change description/file
                // missing) must NOT reload here — that would silently discard whatever the user just
                // typed/toggled right when they need it in place to fix and resubmit (reported live:
                // toggling "Yes" and Saving with no description reverted the switch back to "No").
                if (r.error.errorMessage && r.error.errorMessage.toLowerCase().includes('changed by another user'))
                    this.loadOneSheet();
            }
        });
    };

    handlePrint = () => {
        window.print();
    };

    handleCancel = () => {
        FormManager.checkUnsavedChanges(() => this.props.history.push('/'));
    };

    handleSwitchToWizard = () => {
        FormManager.checkUnsavedChanges(() => {
            this.props.history.push('/certification/certification/CI/' + this.props.match.params.certificationKey);
        });
    };

    // Same SDVOB/VOB designation ConnectToVOBService.cs:73 computes for the public directory
    // listing (`SDVS = x.VeteranServiceConnectedDisabled ? "SDVOB" : "VOB"`), derived from the
    // majority owner (Order 0) here since the one-sheet has no equivalent server-computed field of
    // its own. "SDVET" (the mockup's label) doesn't exist anywhere as a real code in either repo —
    // this uses the real, already-established designation instead.
    renderDesignationBadge = () => {
        const owners = this.state.data.veteranOwnerInfo && this.state.data.veteranOwnerInfo.veteranOwnerList;
        const majorityOwner = owners && owners.find(o => o.order === 0);
        if (!majorityOwner) return null;

        const designation = majorityOwner.veteranServiceConnectedDisabled ? 'SDVOB' : 'VOB';
        return <span id="oneSheetDesignationBadge" className="one-sheet-designation-badge">{designation}</span>;
    };

    renderHeader = () => {
        const { data } = this.state;

        return (
            <Row className="one-sheet-header">
                <Col md={6}>
                    <h3 id="oneSheetHeading">
                        {data.certificationTypeDescription} <small id="oneSheetAppId">App ID {data.certificationId}</small>
                        {' '}{this.renderDesignationBadge()}
                    </h3>
                    <p id="oneSheetRenewalTagline" className="one-sheet-renewal-tagline">Renewals Made Easier</p>
                    <Button id="linkSwitchToWizard" bsStyle="link" className="no-print one-sheet-switch-link" onClick={this.handleSwitchToWizard}>
                        Switch to full multi-page form
                    </Button>
                </Col>
                <Col md={6} className="one-sheet-actions no-print">
                    <Button id="btnPrint" bsStyle="default" onClick={this.handlePrint}><FontAwesomeIcon icon={faPrint} /> Print</Button>
                    {' '}
                    <Button id="btnCancel" bsStyle="default" onClick={this.handleCancel}>Cancel</Button>
                    {' '}
                    <Button id="btnSave" bsStyle="primary" disabled={this.state.saving} onClick={this.handleSave}>Save</Button>
                    {' '}
                    <Button id="btnSubmit" bsStyle="success" disabled={this.state.saving} onClick={this.handleSubmit}>Submit</Button>
                </Col>
            </Row>
        );
    };

    renderCarriedOverNotice = () => {
        const codes = this.state.data.carriedOverStepCodes;
        if (!codes || codes.length === 0) return null;

        return (
            <p id="oneSheetCarriedOverNotice" className="one-sheet-carried-over">
                <em>Banking, Taxes, Equipment, and Articles information was carried over from your prior certification and is not shown here.</em>
            </p>
        );
    };

    render() {
        const { data } = this.state;
        if (this.state.loading || !data) return null;

        return (
            <ContentBody>
                <div className="one-sheet">
                    {this.renderHeader()}

                    {/* Two-column layout matching the "2026 VOCS - New Renewal" mockup: Financials
                        (declaration/revenue/fee) is a top-right block there, not a bottom section —
                        a plain flex layout is used instead of Bootstrap's col-md-* so the columns
                        stay side-by-side at print time too (col-md-* is gated behind a
                        min-width:992px media query, which print layout doesn't reliably satisfy). */}
                    <div className="one-sheet-columns">
                        <div className="one-sheet-col-left">
                            <Box boxStyle="default" title="Company Information">
                                <CompanySection vobInfo={data.vobInfo} onChange={this.onVobInfoChange} />
                            </Box>

                            <Box boxStyle="default" title="Veteran Owners">
                                <VeteranOwnersSection
                                    veteranOwnerList={data.veteranOwnerInfo && data.veteranOwnerInfo.veteranOwnerList}
                                    onChange={this.onVeteranOwnerListChange}
                                    ownershipChange={data.ownershipChange}
                                    ownershipChangeHistory={data.ownershipChangeHistory}
                                    onOwnershipChangeChange={this.onOwnershipChangeChange}
                                />
                            </Box>

                            <Box boxStyle="default" title="Business Certifications">
                                <BusinessCertificationSection businessCertification={data.businessCertification} onChange={this.onBusinessCertificationChange} />
                            </Box>
                        </div>

                        <div className="one-sheet-col-right">
                            <Box boxStyle="default" title="Financials">
                                <FinancialsSection
                                    certificationId={data.certificationId}
                                    vobInfo={data.vobInfo}
                                    declaration={data.declaration}
                                    onVobInfoChange={this.onVobInfoChange}
                                    onDeclarationChange={this.onDeclarationChange}
                                />
                            </Box>

                            <Box boxStyle="default" title="Company Capabilities, Accreditations, NAICS Codes & Employees">
                                <BusinessStructureSection businessStructure={data.businessStructure} onChange={this.onBusinessStructureChange} />
                            </Box>
                        </div>
                    </div>

                    {this.renderCarriedOverNotice()}
                </div>
            </ContentBody>
        );
    }
}

export default OneSheetRenewal;



// WEBPACK FOOTER //
// ./src/areas/certification/oneSheet/OneSheetRenewal.js